import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import Email from '@mui/icons-material/Email'
import PhoneAndroid from '@mui/icons-material/PhoneAndroid'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { AuthLoggedUser } from 'components/AuthLoggedUser/AuthLoggedUser'
import Loader from 'components/Loader/Loader'
import { LuckyBirdGlobalLogo } from 'components/LuckyBirdGlobalLogo'
import { TextMaskCustom } from 'components/TextMaskCustom/TextMaskCustom'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { LoginForm } from 'pages/Login/LoginForm'
import { useIsLuckyBirdGlobalAuth } from 'pages/Login/LuckyBird/useIsLuckyBirdGlobalAuth'
import { redirectOnLogin } from 'pages/Login/redirectOnLogin'
import { FormValues } from 'pages/Login/types'
import { WrapperLoginForm } from 'pages/Login/WrapperLoginForm'
import { getOrGenerateTwoFARememberToken, getTwoFARememberToken } from 'pages/MyAccountEdit/getTwoFARememberToken'
import { usePostEmailVerification } from 'pages/MyAccountEdit/hooks/usePostEmailVerification'
import { usePostPasswordVerification } from 'pages/MyAccountEdit/hooks/usePostPasswordVerification'
import { usePostVerifyTwoFA } from 'pages/MyAccountEdit/hooks/usePostVerifyTwoFA'
import { TwoFAType, TwoFATypes } from 'pages/MyAccountEdit/types'
import { useSubdomainInfo } from 'shared/Site/api'
import { SiteType } from 'shared/Site/siteType'
import { useAuthContext } from 'store/useAuthContext'
import { getResponsiveSize } from 'theme/styles.utils'
import { login } from 'utils/api/apiClient'
import { isLoggedIn, setAuthTokens } from 'utils/axios-jwt-cookies'
import { getThumbnailPath } from 'utils/getThumbnail'
import { valueOrUndefined } from 'utils/getValueOrUndefined'
import { useIsMobile } from 'utils/hooks/useBreakpoints'
import { getProjectWithSocialWallFromUserProjects } from 'utils/hooks/useSocialWall'
import * as Yup from 'yup'

// eslint-disable-next-line complexity
export const LoginPage = () => {
  const [blockedMessage, setBlockedMessage] = useState(null)
  const [isRemember, setIsRemember] = useState(false)
  const [username, setUsername] = useState('')
  const [value, onChange] = useState('')
  const [password, setPassword] = useState('')
  const [isVisiblePassword, setIsVisiblePassword] = useState(false)
  const [selectedType, setSelectedType] = useState<TwoFATypes | null>(null)
  const [selectAlternativeEmail, setSelectAlternativeEmail] = useState(false)
  const [twoFaError, setTwoFaError] = useState<{ code_challenge: string; available_options: TwoFATypes[] } | null>(null)
  const { refetch: refreshUser, user } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { data: subdomainInfo } = useSubdomainInfo()
  const enabledPersonalId = subdomainInfo?.data?.subscriber?.login_field === 'customerInternalId'
  const { mutateAsync: sendEmail, isLoading: isSendingEmail } = usePostEmailVerification()

  const onLogin = async (tokens: { accessToken: string; refreshToken: string }) => {
    setAuthTokens(tokens)
    const data = await refreshUser()
    const isSocialWallActive = !!getProjectWithSocialWallFromUserProjects(data?.data?.data?.projects || {})
    redirectOnLogin(navigate, isSocialWallActive, location?.search)
    enqueueSnackbar(t('auth.login.success'), { variant: 'success' })
    setBlockedMessage(null)
  }

  const { mutate: verifyPassword, isLoading: isPendingVerifyPassword } = usePostPasswordVerification(onLogin)
  const { logoutUser } = useAuthContext()
  const isMobile = useIsMobile()
  const isGlobalLB = useIsLuckyBirdGlobalAuth()
  const subscriber = subdomainInfo?.data?.subscriber

  const { mutate: verify, isLoading: isPendingSetting } = usePostVerifyTwoFA(onLogin)

  const { mutate: loginMutate, isLoading } = useMutation(login, {
    onSuccess: async () => {
      const data = await refreshUser()
      const isSocialWallActive = !!getProjectWithSocialWallFromUserProjects(data?.data?.data?.projects || {})
      redirectOnLogin(navigate, isSocialWallActive, location?.search)
      enqueueSnackbar(t('auth.login.success'), { variant: 'success' })
      setBlockedMessage(null)
    },
    onError: async (error) => {
      if (isAxiosError(error) && error?.status === 401 && error?.response?.data?.error === '2fa_required') {
        setTwoFaError({
          code_challenge: error?.response?.data?.code_challenge,
          available_options: error?.response?.data?.available_options,
        })
      } else {
        if (isAxiosError(error) && error?.status === 429) {
          setBlockedMessage(error?.response?.data?.message ? error?.response?.data?.message : null)
        }
        setTwoFaError(null)
        enqueueSnackbar(t('auth.login.error'), { variant: 'error' })
      }
    },
    useErrorBoundary: false,
  })

  const onSubmit = (values: { username: string; password: string }) => {
    setUsername(values.username)
    loginMutate({ ...values, twoFARememberToken: getTwoFARememberToken(values.username) })
  }

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .label(t(enabledPersonalId ? 'auth.login.personal_id' : 'auth.login.username'))
      .required(),
    password: Yup.string().label(t('auth.login.password')).required(),
  })

  const initialValues: FormValues = {
    username: '',
    password: '',
  }

  const isAlternativeEmail =
    twoFaError?.available_options &&
    twoFaError?.available_options?.length < 2 &&
    twoFaError?.available_options[0] === TwoFAType.googleAuthenticator &&
    selectedType === TwoFAType.email

  const handleSave = () => {
    if (isAlternativeEmail) {
      setSelectedType(null)
    } else {
      verify({
        code: String(value),
        code_challenge: twoFaError?.code_challenge || '',
        twoFARememberToken: isRemember ? getOrGenerateTwoFARememberToken(username) : null,
        type: selectedType || twoFaError?.available_options?.[0] || null,
      })
    }
  }
  const Logo = () => (
    <Typography color="primary" variant="h1" align="center">
      {subscriber?.logo_path ? (
        <img
          src={getThumbnailPath(subscriber?.logo_path)}
          alt={subscriber?.name}
          style={{
            maxHeight: getResponsiveSize(400),
            width: getResponsiveSize(400),
            maxWidth: isMobile ? '80vw' : '100%',
          }}
        />
      ) : (
        subscriber?.name
      )}
    </Typography>
  )

  if (isLoggedIn() && !!user) return <AuthLoggedUser />

  if (['uz-leuven.arteel.com'].includes(window.location.host) && '/auth/login' === location.pathname) {
    return <Navigate to="/auth/luckybird" />
  }

  const selectedMethod = selectAlternativeEmail
    ? selectedType
    : twoFaError?.available_options
      ? twoFaError?.available_options?.length < 2
        ? twoFaError?.available_options?.[0]
        : selectedType
      : 0

  if (selectAlternativeEmail && !selectedType) {
    return (
      <Loader isLoading={isLoading || subdomainInfo?.data?.type === SiteType.project} fullHeight>
        <Box
          display="grid"
          gridTemplateRows="1fr auto"
          gridTemplateColumns="1fr"
          justifyItems="center"
          alignItems="center"
          height={valueOrUndefined(!isMobile, '100%')}
        >
          <Box width="100%">
            <Grid container justifyContent="center">
              <Grid item md={8} lg={6} xl={5}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>{isGlobalLB ? <LuckyBirdGlobalLogo /> : <Logo />}</Grid>
                  <Grid item>
                    <Box
                      mt={2}
                      px={4}
                      maxWidth={400}
                      gridTemplateColumns={{ xs: '1fr' }}
                      justifyContent="center"
                      mx="auto"
                      display="grid"
                      columnGap={2}
                      rowGap={2}
                    >
                      <Box>
                        <Typography fontWeight="bold">
                          {t('profile.two_factor_authentication.login.another_method_text')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography>{t('profile.two_factor_authentication.login.another_method_text2')}</Typography>
                      </Box>
                      <Box>
                        <TextField
                          autoComplete="off"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          name="password"
                          label={t('auth.login.password')}
                          type={isVisiblePassword ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setIsVisiblePassword((prev) => !prev)}>
                                  {isVisiblePassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                        />
                      </Box>
                      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            setSelectAlternativeEmail(false)
                            setSelectedType(null)
                            setPassword('')
                            onChange('')
                          }}
                        >
                          {t('app.back')}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            verifyPassword({
                              code_challenge: twoFaError?.code_challenge || '',
                              password,
                            })
                          }
                          loading={isPendingVerifyPassword}
                        >
                          {t('profile.two_factor_authentication.verify')}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Loader>
    )
  }

  if (twoFaError?.code_challenge && (twoFaError?.available_options?.length < 2 || !!selectedType)) {
    return (
      <Loader isLoading={isLoading || subdomainInfo?.data?.type === SiteType.project} fullHeight>
        <Box
          display="grid"
          gridTemplateRows="1fr auto"
          gridTemplateColumns="1fr"
          justifyItems="center"
          alignItems="center"
          height={valueOrUndefined(!isMobile, '100%')}
        >
          <Box width="100%">
            <Grid container justifyContent="center">
              <Grid item md={8} lg={6} xl={5}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>{isGlobalLB ? <LuckyBirdGlobalLogo /> : <Logo />}</Grid>
                  <Grid item>
                    <Box
                      mt={8}
                      px={isMobile ? 2 : 16}
                      gridTemplateColumns="1fr"
                      display="grid"
                      columnGap={2}
                      rowGap={2}
                      mx="auto"
                    >
                      {selectedMethod === TwoFAType.googleAuthenticator && (
                        <Typography>{t('profile.two_factor_authentication.enter_code.app')}</Typography>
                      )}
                      {selectedMethod === TwoFAType.email && (
                        <Typography>{t('profile.two_factor_authentication.enter_code.email')}</Typography>
                      )}
                      <TextField
                        autoComplete="off"
                        value={value}
                        onChange={(event) => onChange(event.target.value)}
                        name="textmask"
                        label={t('profile.two_factor_authentication.code')}
                        size="small"
                        slotProps={{
                          input: { inputComponent: TextMaskCustom as any, inputProps: { handleSubmit: handleSave } },
                        }}
                      />
                      {selectedMethod === TwoFAType.googleAuthenticator && (
                        <Typography>{t('profile.two_factor_authentication.description4')}</Typography>
                      )}
                      {selectedMethod === TwoFAType.email && (
                        <Typography>
                          {t('profile.two_factor_authentication.resend_code.text')}{' '}
                          <Typography
                            color="primary"
                            fontWeight="bold"
                            component="span"
                            sx={{ cursor: 'pointer' }}
                            onClick={async () => {
                              await sendEmail({ code_challenge: twoFaError?.code_challenge || '' })
                              enqueueSnackbar(t('profile.two_factor_authentication.resend_email'), {
                                variant: 'success',
                              })
                            }}
                          >
                            {t('profile.two_factor_authentication.resend_code')}
                          </Typography>
                        </Typography>
                      )}
                      {!isAlternativeEmail && (
                        <Box>
                          <FormControlLabel
                            control={<Checkbox value={isRemember} onChange={() => setIsRemember((prev) => !prev)} />}
                            label={t('profile.two_factor_authentication.remember_me')}
                          />
                        </Box>
                      )}
                      <Box gridTemplateColumns="1fr 1fr" display="grid" columnGap={2} rowGap={1}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            setSelectAlternativeEmail(false)
                            setSelectedType(null)
                            setPassword('')
                            onChange('')
                            if (twoFaError?.available_options?.length < 2) {
                              logoutUser()
                            }
                          }}
                          loading={isPendingSetting}
                        >
                          {t('app.back')}
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSave} loading={isPendingSetting}>
                          {t('profile.two_factor_authentication.verify')}
                        </Button>
                      </Box>
                      {twoFaError?.available_options?.length < 2 &&
                        twoFaError?.available_options[0] === TwoFAType.googleAuthenticator &&
                        selectedType !== TwoFAType.email && (
                          <Box display="grid">
                            <Link
                              align="center"
                              variant="body1"
                              color="primary"
                              underline="hover"
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                setSelectedType(TwoFAType.email)
                                setSelectAlternativeEmail(true)
                                sendEmail({ code_challenge: twoFaError?.code_challenge || '' })
                              }}
                            >
                              {t('profile.two_factor_authentication.login.another_method_link')}
                            </Link>
                          </Box>
                        )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Loader>
    )
  }

  if (twoFaError?.code_challenge && twoFaError?.available_options?.length >= 2 && !selectedType) {
    return (
      <Loader isLoading={isLoading || subdomainInfo?.data?.type === SiteType.project} fullHeight>
        <Box
          display="grid"
          gridTemplateRows="1fr auto"
          gridTemplateColumns="1fr"
          justifyItems="center"
          alignItems="center"
          height={valueOrUndefined(!isMobile, '100%')}
          id="main-box-2fa"
        >
          <Box width="100%">
            <Grid container justifyContent="center">
              <Grid item md={8} lg={6} xl={5} id="grid-item-box-2fa">
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>{isGlobalLB ? <LuckyBirdGlobalLogo /> : <Logo />}</Grid>
                  <Grid item>
                    <Box
                      mt={6}
                      px={isMobile ? 2 : 16}
                      gridTemplateColumns="1fr auto"
                      display="grid"
                      columnGap={2}
                      rowGap={1}
                    >
                      <Box>
                        <Typography variant="h3" align="center">
                          {t('profile.two_factor_authentication.choose_method')}
                        </Typography>
                        <List>
                          {twoFaError?.available_options?.map((type) => {
                            switch (type) {
                              case TwoFAType.email:
                                return (
                                  <ListItemButton
                                    sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}
                                    onClick={async () => {
                                      await sendEmail({ code_challenge: twoFaError?.code_challenge || '' })
                                      setSelectedType(TwoFAType.email)
                                    }}
                                  >
                                    <ListItemIcon>
                                      {isSendingEmail ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <Email />}
                                    </ListItemIcon>
                                    <ListItemText primary={t(`profile.two_factor_authentication.${type}`)} />
                                  </ListItemButton>
                                )
                              case TwoFAType.googleAuthenticator:
                                return (
                                  <ListItemButton
                                    sx={{
                                      border: (theme) => `1px solid ${theme.palette.divider}`,
                                      borderBottom: 'none',
                                    }}
                                    onClick={() => setSelectedType(TwoFAType.googleAuthenticator)}
                                  >
                                    <ListItemIcon>
                                      <PhoneAndroid />
                                    </ListItemIcon>
                                    <ListItemText primary={t(`profile.two_factor_authentication.${type}`)} />
                                  </ListItemButton>
                                )
                            }
                            return (
                              <FormControlLabel
                                key={type}
                                control={<Checkbox value={type} onChange={() => setSelectedType(type)} />}
                                label={t(`profile.two_factor_authentication.${type}`)}
                              />
                            )
                          })}
                        </List>
                        <Box display="grid" mt={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              setPassword('')
                              onChange('')
                              logoutUser()
                            }}
                          >
                            {t('app.back')}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Loader>
    )
  }

  return (
    <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={onSubmit}>
      <Form style={{ height: '100%' }}>
        <WrapperLoginForm>
          <LoginForm blockedMessage={blockedMessage} isPending={isLoading} />
        </WrapperLoginForm>
      </Form>
    </Formik>
  )
}
